import { FormGroup, Input } from "reactstrap";
import { useState, useEffect } from "react";
import { Form, SelectPicker } from "rsuite";

import FileHandler from "../../FormBuilder/CustomizationTab/BuildTab/components/FileHandler";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import CustomToggle from "../../Drawers/Fields/CustomToggle";
import RequiredTitle from "elements/RequiredTitle";
import TenantAPI from "../../../api/api/TenantAPI";
import StrUtils from "utils/StrUtils";

const PrivacyData = [
  {
    label: "Public",
    description: "Any member in this workspace can view this page.",
    value: "public",
    icon: "mdi mdi-earth",
  },
  {
    label: "Private",
    description: "Only the people you add can view this page.",
    value: "private",
    icon: "mdi mdi-lock",
  },
];

const PageEditor = ({
  onChange,
  resetData,
  setResetData,
  existingPage,
  disabled = false,
  onSubmit,
}) => {
  // ID for the file input
  const [uniqueID] = useState(StrUtils.uuidv4());
  const [pageName, setPageName] = useState("");
  const [pageDescription, setPageDescription] = useState("");
  const [pagePrivacy, setPagePrivacy] = useState("public");
  const [useIcon, setUseIcon] = useState(false);
  const [file, setFile] = useState(null);

  const passDataToParent = (
    pageName,
    pageDescription,
    pagePrivacy,
    pageFile,
    useIcon
  ) => {
    let payload = {
      Name: pageName,
      Description: pageDescription,
      Privacy: pagePrivacy,
    };

    if (!useIcon && existingPage?.IconURL) {
      payload.IconURL = null;
    } else if (useIcon && pageFile) {
      payload.IconURL = pageFile;
    } else if (!pageFile) {
      payload.IconURL = null;
    }

    onChange(payload);
  };

  const handleNameChange = (e) => {
    setPageName(e.target.value);
    passDataToParent(
      e.target.value,
      pageDescription,
      pagePrivacy,
      file,
      useIcon
    );
  };

  const handleDescriptionChange = (e) => {
    setPageDescription(e.target.value);
    passDataToParent(pageName, e.target.value, pagePrivacy, file, useIcon);
  };

  const handlePrivacyChange = (privacy) => {
    setPagePrivacy(privacy);
    passDataToParent(pageName, pageDescription, privacy, file, useIcon);
  };

  const handleFileChange = (file) => {
    let newFile = file?.files[0];

    passDataToParent(pageName, pageDescription, pagePrivacy, newFile, useIcon);
  };

  const handleIconChange = () => {
    let newVal = !useIcon;

    setUseIcon(newVal);
    if (!newVal && file) {
      try {
        TenantAPI.deleteFiles({
          type: "blobName",
          blobName: file,
          recordID: existingPage?.id ?? "",
          moduleAPIName: existingPage ? "Pages" : "",
          fieldAPIName: existingPage ? "IconURL" : "",
        });
      } catch (error) {}

      setFile(null);
    }

    passDataToParent(pageName, pageDescription, pagePrivacy, file, newVal);
  };

  useEffect(() => {
    if (resetData === true) {
      setPageName("");
      setFile(null);
      setUseIcon(false);
      setResetData(false);
    }
  }, [resetData, setResetData]);

  useEffect(() => {
    if (existingPage !== null) {
      setPageName(existingPage?.Name ?? "");
      setPageDescription(existingPage?.Description ?? "");
      setUseIcon(existingPage?.IconURL ? true : false);
      setPagePrivacy(existingPage?.Privacy ?? "public");
      setFile(existingPage?.IconURL ? existingPage?.IconURL : "");
    }
  }, [existingPage]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        callFunctionIfExists(onSubmit);
      }}
    >
      <RequiredTitle title="Name" size="md" />
      <Input
        autoFocus
        placeholder="Enter page name"
        value={pageName}
        onChange={handleNameChange}
        disabled={disabled}
        className="mb-4"
      />

      <h4>Description</h4>
      <Input
        placeholder="Enter page description"
        value={pageDescription}
        onChange={handleDescriptionChange}
        type="textarea"
        disabled={disabled}
        className="mb-4"
      />

      <RequiredTitle title="Privacy" size="md" />
      <SelectPicker
        className="mb-4"
        value={pagePrivacy}
        onChange={handlePrivacyChange}
        searchable={false}
        data={PrivacyData}
        preventOverflow
        disabled={disabled}
        cleanable={false}
        block
        renderMenuItem={(_, item) => {
          return (
            <div className="d-flex align-items-center">
              <i
                className={item.icon}
                style={{ fontSize: 26, paddingRight: 14 }}
              />
              <div>
                <span className="font-weight-600">{item.label}</span>
                <br />
                <span className="font-weight-500" style={{ fontSize: 14 }}>
                  {item.description}
                </span>
              </div>
            </div>
          );
        }}
      />
      <h4>Custom Icon</h4>
      <CustomToggle
        value={useIcon}
        handleChange={handleIconChange}
        disabled={disabled}
      />
      {useIcon && (
        <FileHandler
          recordID={existingPage?.id ?? ""}
          moduleAPIName="Pages"
          key={uniqueID}
          file={file}
          setFile={setFile}
          handleFileData={handleFileChange}
          publicContainer={true}
          sources={
            !!existingPage
              ? [
                  {
                    Source: "Record",
                    FieldAPIName: "IconURL",
                    SourceID: existingPage.id,
                    SourceModule: "Pages",
                  },
                ]
              : undefined
          }
          fieldAPIName="IconURL"
        />
      )}
      <button type="submit" className="tw:hidden"></button>
    </form>
  );
};

export default PageEditor;
